import { Component, OnInit } from '@angular/core';
import { SharedModule } from '../../utils/shared.module';
import { KeyboardComponent } from '../../utils/keyboard/keyboard.component';
import { ThemesComponent } from '../themes/themes.component';
import { WheelCell } from '../wheel/wheel.model';
import { Theme } from '../../data/theme.model';
import { StoreService } from '../../data/store.service';
import { MsgService } from '../../utils/message/msg.service';
import { ApiService } from '../../data/api.service';
import { UtilsService } from '../../utils/utils.service';
import { I18ns } from '../../utils/i18n/I18n';
import { LetterBlock } from './melo.model';

@Component({
  selector: 'app-melo',
  standalone: true,
  imports: [SharedModule, KeyboardComponent, ThemesComponent],
  templateUrl: './melo.component.html',
  styleUrl: './melo.component.scss',
})
export class MeloComponent implements OnInit {
  wordIndex = 0; // Over 9000
  dictionary: string[] = [];
  history: string[] = [];
  cells: WheelCell[][] = [];
  letters: LetterBlock[] = [];
  words: string[] = [];
  theme?: Theme;
  currentLetter = ' ';
  wrongLetterCounter = 0;
  isGameLost = false;
  isGameWon = false;
  gameIsPaused = false;
  difficulty = 2;
  lineIndex = 0;
  cellIndex = 0;
  protected readonly I18ns = I18ns;
  private readonly turns = 6;
  private readonly pieSize = 100 / this.turns;

  constructor(
    public readonly store: StoreService,
    private readonly msg: MsgService,
    private readonly api: ApiService,
    private readonly utils: UtilsService,
  ) {}

  ngOnInit() {
    if (!this.store.theme()?.id) {
      this.msg.add({
        message: I18ns.grid.noTheme,
        severity: 'error',
      });
      return;
    }
    this.msg.clear();

    this.theme = this.store.theme();
    this.dictionary = this.utils.shuffle(this.theme.words.split(','));

    this.newWords();
  }

  keyPressed(key: string) {
    if (key === 'Backspace') {
    } else {
      this.addLetter(key);
    }
  }

  getPieColor() {
    const output = [];
    const defaultColor = '255, 255, 255';
    const errorColor = '253, 114, 133';

    for (let i = 1; i <= this.turns; i++) {
      const color1 = this.wrongLetterCounter < i ? errorColor : defaultColor;
      const color2 = this.wrongLetterCounter <= i ? errorColor : defaultColor;
      output.push(`rgba(${color1}, ${i * 0.05 + 0.5}) ${i * this.pieSize}%`);
      output.push(`rgba(${color2}, ${(i + 1) * 0.05 + 0.5}) ${i * this.pieSize}%`);
    }

    output.pop();
    return `conic-gradient(${output.join(', ')})`;
  }

  setLine(i: number) {
    if (this.wordIsComplete(i)) {
      return false;
    }
    this.lineIndex = i;

    return true;
  }

  private newWords() {
    if (this.dictionary.length === 0) {
      return;
    }
    this.msg.clear();
    this.history = [];
    this.lineIndex = 0;
    this.cellIndex = 0;
    this.wrongLetterCounter = 0;

    this.words = this.dictionary.slice(this.wordIndex, this.wordIndex + this.difficulty);
    this.letters = this.utils.shuffle(this.words.map((word: string) => word.split('')).flat()).map((l) => ({
      letter: l,
      found: false,
    }));

    this.wordIndex =
      this.wordIndex + this.difficulty < this.dictionary.length
        ? this.wordIndex + this.difficulty
        : this.dictionary.length;

    this.cells = this.words.map((w) =>
      w.split('').map((letter: string) => {
        return {
          letter: letter,
          display: '',
        };
      }),
    );
  }

  private addLetter(key: string) {
    if (this.gameIsPaused) {
      return;
    }

    this.currentLetter = key;

    if (this.cells[this.lineIndex][this.cellIndex].letter === key) {
      this.cells[this.lineIndex][this.cellIndex].display = key;
      this.cellIndex++;

      const lb = this.letters.find((l) => l.letter === key && !l.found);
      if (lb) {
        lb.found = true;
      }

      if (this.wordIsComplete(this.lineIndex)) {
        this.api.updateWordFoundCount(this.theme?.id).subscribe();

        for (let i = 0; i < this.cells.length; i++) {
          if (this.setLine(i)) {
            this.cellIndex = 0;
            return;
          }
        }
        // ALL LINES FOUND
        if (this.wordIndex + 1 > this.dictionary.length) {
          // ALL WORDS FOUND
          this.isGameWon = true;
        } else {
          this.newWords();
        }
      }
    } else {
      this.wrongLetterCounter++;
    }

    if (this.wrongLetterCounter === this.turns) {
      this.isGameLost = true;
    }
  }

  private wordIsComplete(index: number) {
    return this.cells[index].every((cell) => cell.display !== '');
  }
}
